import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, MapPin, Monitor, Copy, Check, Smartphone, Calendar, MessageSquare, Languages, Globe, Trash2, RefreshCw } from 'lucide-react';

interface MetadataType {
  modelUsed?: string; // Model used for processing (can be at root level)
  inputType?: string; // 'microphone' | 'youtube' | 'file_upload' | 'app_share'
  headers?: {
    realIp?: string;
    source?: string;
    paidUser?: string;
    useTurbo?: string;
    userAgent?: string;
    country?: string;
    timezone?: string;
    city?: string;
    chat_history?: string;
    mode?: string;
    // Device-specific fields
    androidVersion?: string;
    appBuild?: string;
    appVersion?: string;
    deviceBrand?: string;
    deviceId?: string;
    deviceManufacturer?: string;
    deviceModel?: string;
    platform?: string;
    sdkVersion?: string;
    systemVersion?: string;
    systemName?: string;
    architecture?: string;
    transcriptionModel?: string;
    transcriptionService?: string;
    modelUsed?: string; // Also support modelUsed inside headers for backwards compatibility
  };
  cf?: {
    country?: string | null;
    timezone?: string | null;
    region?: string | null;
    city?: string | null;
  };
}

interface LogEntry {
  id: number;
  request_id: number;
  request_body: string;
  text: string | null;
  output_text: string | null;
  chat_history: string | null;
  created_at: string;
  metadata: string;
  cost: number;
}

interface Props {
  log: LogEntry;
  metadata: MetadataType;
  isExpanded?: boolean;
  onViewChatHistory?: () => void;
  onDeviceIdClick?: (deviceId: string) => void;
  onDeleteLog?: (requestId: number) => void;
  isDeleting?: boolean;
}

const MobileLogCard: React.FC<Props> = ({ log, metadata, isExpanded = false, onViewChatHistory, onDeviceIdClick, onDeleteLog, isDeleting = false }) => {
  const [expanded, setExpanded] = useState(isExpanded);
  const [copiedInput, setCopiedInput] = useState(false);
  const [copiedOutput, setCopiedOutput] = useState(false);
  const [showFullInput, setShowFullInput] = useState(false);
  const [showFullOutput, setShowFullOutput] = useState(false);
  
  // Translation states
  const [translatedInput, setTranslatedInput] = useState<string | null>(null);
  const [translatedOutput, setTranslatedOutput] = useState<string | null>(null);
  const [isTranslatingInput, setIsTranslatingInput] = useState(false);
  const [isTranslatingOutput, setIsTranslatingOutput] = useState(false);
  const [showTranslatedInput, setShowTranslatedInput] = useState(false);
  const [showTranslatedOutput, setShowTranslatedOutput] = useState(false);

  // Text collapse threshold (characters)
  const TEXT_COLLAPSE_THRESHOLD = 200;

  // Update expanded state when isExpanded prop changes
  useEffect(() => {
    setExpanded(isExpanded);
  }, [isExpanded]);

  const decodeUTF8 = (str: string) => {
    try {
      return decodeURIComponent(escape(str));
    } catch (e) {
      return str;
    }
  };

  const formatLocation = () => {
    const parts = [
      metadata?.headers?.country,
      metadata?.headers?.city,
      metadata?.headers?.timezone
    ].filter(Boolean).map(str => str ? decodeUTF8(str) : '');
    
    return parts.join(', ') || 'N/A';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { 
      month: 'short', 
      day: 'numeric'
    }) + ' ' + date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCopy = async (text: string | null, type: 'input' | 'output') => {
    let textToCopy = text || '';
    
    // If translation is shown and available, copy the translated text instead
    if (type === 'input' && showTranslatedInput && translatedInput) {
      textToCopy = translatedInput;
    } else if (type === 'output' && showTranslatedOutput && translatedOutput) {
      textToCopy = translatedOutput;
    }
    
    await navigator.clipboard.writeText(textToCopy);
    if (type === 'input') {
      setCopiedInput(true);
      setTimeout(() => setCopiedInput(false), 2000);
    } else {
      setCopiedOutput(true);
      setTimeout(() => setCopiedOutput(false), 2000);
    }
  };

  const handleTranslate = async (text: string, type: 'input' | 'output') => {
    if (type === 'input') {
      setIsTranslatingInput(true);
    } else {
      setIsTranslatingOutput(true);
    }

    try {
      const response = await fetch('https://openai-assistants-cloudfare-worker-boilerplate.chris-172.workers.dev/claude', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          input: text,
          instructionText: "Translate the following text to English. If the text is already in English, translate it to the language best suited for the user based on context. Provide only the translation without any explanations.",
          savedMessages: [{ role: 'user', content: text }]
        }),
      });

      if (!response.ok) {
        throw new Error('Translation failed');
      }

      const translatedText = await response.text();
      
      if (type === 'input') {
        setTranslatedInput(translatedText);
        setShowTranslatedInput(true);
      } else {
        setTranslatedOutput(translatedText);
        setShowTranslatedOutput(true);
      }
    } catch (error) {
      console.error('Translation error:', error);
      // Handle error appropriately (e.g., show error message)
    } finally {
      if (type === 'input') {
        setIsTranslatingInput(false);
      } else {
        setIsTranslatingOutput(false);
      }
    }
  };

  const hasChatHistory = () => {
    return !!log?.chat_history;
  };

  // Safe text and output values with null checks
  const safeText = log?.text || '';
  const safeOutputText = log?.output_text || '';

  // Add function to render collapsible text
  const renderCollapsibleText = (text: string, showFull: boolean, setShowFull: (show: boolean) => void) => {
    if (text.length <= TEXT_COLLAPSE_THRESHOLD) {
      return text;
    }

    if (showFull) {
      return (
        <>
          {text}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowFull(false);
            }}
            className="block mt-2 text-blue-400 hover:text-blue-300 text-sm"
          >
            Show less
          </button>
        </>
      );
    }

    return (
      <>
        {text.slice(0, TEXT_COLLAPSE_THRESHOLD)}...
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowFull(true);
          }}
          className="block mt-2 text-blue-400 hover:text-blue-300 text-sm"
        >
          Read more
        </button>
      </>
    );
  };

  return (
    <div className={`bg-gray-700 rounded-lg transition-all duration-200 ${expanded ? 'p-3' : 'p-1.5'}`}>
      <button 
        onClick={() => setExpanded(!expanded)}
        className="w-full"
      >
        {!expanded ? (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 justify-between">
              <div className="flex items-center gap-2 min-w-0 flex-grow">
              <div className="bg-blue-500/10 px-2 py-0.5 rounded text-blue-400 whitespace-nowrap text-xs">
                  #{log.request_id}
                </div>
                
                {metadata?.headers?.paidUser === 'true' && (
                  <div className="px-1.5 py-0.5 rounded text-[10px] font-medium text-emerald-400">
                    Paid
                  </div>
                )}
                {metadata?.headers?.useTurbo === 'true' && (
                  <div className="px-1.5 py-0.5 rounded text-[10px] font-medium text-blue-400">
                    Turbo
                  </div>
                )}
                <div className="flex items-center gap-1 text-gray-400 text-xs">
                  <MapPin className="w-3 h-3" />
                  <span>{metadata?.headers?.country && metadata?.headers?.country.split(',')[0]?.trim()}</span>
                </div>
                {metadata?.headers?.mode && (
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                    metadata?.headers?.mode === 'chat' ? 'bg-blue-500/20 text-blue-400' :
                    metadata?.headers?.mode === 'feedback' ? 'bg-yellow-500/20 text-yellow-400' :
                    metadata?.headers?.mode === 'summary' ? 'bg-green-500/20 text-green-400' :
                    'bg-gray-500/20 text-gray-400'
                  }`}>
                    {metadata?.headers?.mode}
                  </span>
                )}
                {metadata?.headers?.transcriptionService && (
                  <span className="text-[10px] text-gray-400">
                    {metadata.headers.transcriptionService}
                  </span>
                )}
                <div className="text-xs font-medium text-gray-400">
                  ${Number(log.cost).toFixed(2)}
                </div>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-500" />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-400 text-xs">
                <Calendar className="w-3 h-3 flex-shrink-0" />
                <span>{formatDate(log.created_at)}</span>
                <span className="text-gray-500">{metadata?.headers?.platform || ''}</span>
                <span className="text-gray-500">({safeText.length}/{safeOutputText.length})</span>
              </div>
              
              {hasChatHistory() && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onViewChatHistory) {
                      onViewChatHistory();
                    }
                  }}
                  className="flex items-center gap-1 text-blue-400 hover:text-blue-300 transition-colors"
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex justify-between items-center w-full">
              <div className="bg-blue-500/10 px-2 py-0.5 rounded text-blue-400">
                #{log.request_id}
              </div>
              <div className="flex items-center gap-2">
                <div className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  metadata?.headers?.paidUser === 'true'
                    ? 'bg-emerald-400/10 text-emerald-400'
                    : 'bg-gray-500/20 text-gray-400'
                }`}>
                  {metadata?.headers?.paidUser === 'true' ? 'Paid User' : 'Free User'}
                </div>
                <div className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  metadata?.headers?.useTurbo === 'true'
                    ? 'bg-blue-400/10 text-blue-400'
                    : 'bg-gray-500/20 text-gray-400'
                }`}>
                  {metadata?.headers?.useTurbo === 'true' ? 'Turbo' : 'Standard'}
                </div>
              </div>
            </div>
            
            <div className="grid gap-2 text-sm">
              <div className="flex items-center gap-2">
                <Monitor className="w-4 h-4 text-emerald-400" />
                <span className="text-gray-300">{metadata?.headers?.source || 'N/A'}</span>
                {metadata?.headers?.mode && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    metadata?.headers?.mode === 'chat' ? 'bg-blue-500/20 text-blue-400' :
                    metadata?.headers?.mode === 'feedback' ? 'bg-yellow-500/20 text-yellow-400' :
                    metadata?.headers?.mode === 'summary' ? 'bg-green-500/20 text-green-400' :
                    'bg-gray-500/20 text-gray-400'
                  }`}>
                    {metadata?.headers?.mode}
                  </span>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-purple-400" />
                <span className="text-gray-300 text-left">{formatLocation()}</span>
              </div>

              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-orange-400" />
                <span className="text-gray-300">{metadata?.headers?.realIp || 'N/A'}</span>
              </div>

              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-400" />
                <span className="text-gray-300">{new Date(log.created_at).toLocaleString()}</span>
              </div>
            </div>

            <div className="flex justify-center">
              <ChevronUp className="w-5 h-5 text-gray-400" />
            </div>
          </div>
        )}
      </button>

      {/* Expandable content with new copy buttons */}
      {expanded && (
        <div className="space-y-3 pt-3 mt-3 border-t border-gray-600">
          <div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400 text-sm font-medium">Input Text:</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{safeText.length} characters</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopy(log.text, 'input');
                  }}
                  className="p-1 hover:bg-gray-600 rounded transition-colors"
                >
                  {copiedInput ? (
                    <Check className="w-4 h-4 text-green-400" />
                  ) : (
                    <Copy className="w-4 h-4 text-gray-400" />
                  )}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (translatedInput && showTranslatedInput) {
                      setShowTranslatedInput(false);
                    } else if (translatedInput) {
                      setShowTranslatedInput(true);
                    } else {
                      handleTranslate(safeText, 'input');
                    }
                  }}
                  className={`p-1 hover:bg-gray-600 rounded transition-colors ${isTranslatingInput ? 'animate-pulse' : ''}`}
                  disabled={isTranslatingInput}
                >
                  <Languages className={`w-4 h-4 ${showTranslatedInput ? 'text-blue-400' : 'text-gray-400'}`} />
                </button>
              </div>
            </div>
            {showTranslatedInput && translatedInput && (
              <div className="flex gap-2 mt-1 text-xs">
                <button
                  onClick={() => setShowTranslatedInput(false)}
                  className={`px-2 py-1 rounded ${!showTranslatedInput ? 'bg-gray-600 text-white' : 'text-gray-400'}`}
                >
                  Original
                </button>
                <button
                  onClick={() => setShowTranslatedInput(true)}
                  className={`px-2 py-1 rounded ${showTranslatedInput ? 'bg-gray-600 text-white' : 'text-gray-400'}`}
                >
                  Translated
                </button>
              </div>
            )}
            <div className="text-white mt-1 bg-gray-800 p-3 rounded-lg text-sm">
              {renderCollapsibleText(
                showTranslatedInput && translatedInput ? translatedInput : safeText,
                showFullInput,
                setShowFullInput
              )}
            </div>
          </div>
          
          <div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400 text-sm font-medium">Output:</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{safeOutputText.length} characters</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopy(log.output_text, 'output');
                  }}
                  className="p-1 hover:bg-gray-600 rounded transition-colors"
                >
                  {copiedOutput ? (
                    <Check className="w-4 h-4 text-green-400" />
                  ) : (
                    <Copy className="w-4 h-4 text-gray-400" />
                  )}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (translatedOutput && showTranslatedOutput) {
                      setShowTranslatedOutput(false);
                    } else if (translatedOutput) {
                      setShowTranslatedOutput(true);
                    } else {
                      handleTranslate(safeOutputText, 'output');
                    }
                  }}
                  className={`p-1 hover:bg-gray-600 rounded transition-colors ${isTranslatingOutput ? 'animate-pulse' : ''}`}
                  disabled={isTranslatingOutput}
                >
                  <Languages className={`w-4 h-4 ${showTranslatedOutput ? 'text-blue-400' : 'text-gray-400'}`} />
                </button>
              </div>
            </div>
            {showTranslatedOutput && translatedOutput && (
              <div className="flex gap-2 mt-1 text-xs">
                <button
                  onClick={() => setShowTranslatedOutput(false)}
                  className={`px-2 py-1 rounded ${!showTranslatedOutput ? 'bg-gray-600 text-white' : 'text-gray-400'}`}
                >
                  Original
                </button>
                <button
                  onClick={() => setShowTranslatedOutput(true)}
                  className={`px-2 py-1 rounded ${showTranslatedOutput ? 'bg-gray-600 text-white' : 'text-gray-400'}`}
                >
                  Translated
                </button>
              </div>
            )}
            <div className="text-white mt-1 bg-gray-800 p-3 rounded-lg text-sm">
              {renderCollapsibleText(
                showTranslatedOutput && translatedOutput ? translatedOutput : safeOutputText,
                showFullOutput,
                setShowFullOutput
              )}
            </div>
          </div>

          {hasChatHistory() && (
            <div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-sm font-medium">Chat History:</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onViewChatHistory) {
                      onViewChatHistory();
                    }
                  }}
                  className="flex items-center gap-2 bg-blue-500/20 text-blue-400 px-2 py-1 rounded text-xs"
                >
                  <MessageSquare className="w-4 h-4" />
                  View Conversation
                </button>
              </div>
            </div>
          )}

          <div className="bg-gray-800 p-3 rounded-lg space-y-2 text-xs">
            <div className="text-gray-300 font-medium mb-2">Device Information</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              <div className="text-gray-400">Platform: <span className="text-gray-300">{metadata?.headers?.platform || 'N/A'}</span></div>
              <div className="text-gray-400">Android: <span className="text-gray-300">{metadata?.headers?.androidVersion || 'N/A'}</span></div>
              <div className="text-gray-400">App Version: <span className="text-gray-300">{metadata?.headers?.appVersion || 'N/A'}</span></div>
              <div className="text-gray-400">Build: <span className="text-gray-300">{metadata?.headers?.appBuild || 'N/A'}</span></div>
              <div className="text-gray-400">Brand: <span className="text-gray-300">{metadata?.headers?.deviceBrand || 'N/A'}</span></div>
              <div className="text-gray-400">Model: <span className="text-gray-300">{metadata?.headers?.deviceModel || 'N/A'}</span></div>
              <div className="text-gray-400">SDK: <span className="text-gray-300">{metadata?.headers?.sdkVersion || 'N/A'}</span></div>
              <div className="text-gray-400">System: <span className="text-gray-300">{metadata?.headers?.systemName || 'N/A'}</span></div>
              <div className="text-gray-400 col-span-2">System Version: <span className="text-gray-300">{metadata?.headers?.systemVersion || 'N/A'}</span></div>
              <div className="text-gray-400">Architecture: <span className="text-gray-300">{metadata?.headers?.architecture || 'N/A'}</span></div>
              <div
                className="text-gray-400 cursor-pointer hover:text-blue-400"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onDeviceIdClick && metadata?.headers?.deviceId) {
                    onDeviceIdClick(metadata.headers.deviceId);
                  }
                }}
              >
                Device ID: <span className="text-blue-400 hover:text-blue-300">{metadata?.headers?.deviceId || 'N/A'}</span>
              </div>
            </div>
          </div>

          {(metadata?.headers?.transcriptionModel ||
            metadata?.headers?.transcriptionService ||
            metadata?.modelUsed ||
            metadata?.headers?.modelUsed ||
            metadata?.inputType) && (
            <div className="bg-gray-800 p-3 rounded-lg space-y-2 text-xs">
              <div className="text-gray-300 font-medium mb-2">Model Information</div>
              <div className="space-y-2">
                {metadata?.headers?.transcriptionService && (
                  <div className="text-gray-400">
                    Transcription Service:
                    <span className="text-gray-300 ml-2">{metadata.headers.transcriptionService}</span>
                  </div>
                )}
                {metadata?.headers?.transcriptionModel && (
                  <div className="text-gray-400">
                    Transcription Model:
                    <span className="text-gray-300 ml-2 break-all">{metadata.headers.transcriptionModel}</span>
                  </div>
                )}
                {(metadata?.modelUsed || metadata?.headers?.modelUsed) && (
                  <div className="text-gray-400">
                    Processing Model:
                    <span className="text-gray-300 ml-2 break-all">{metadata?.modelUsed || metadata?.headers?.modelUsed}</span>
                  </div>
                )}
                {metadata?.inputType && (
                  <div className="text-gray-400">
                    Input Type:
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                      metadata.inputType === 'microphone'  ? 'bg-purple-500/20 text-purple-400' :
                      metadata.inputType === 'youtube'     ? 'bg-red-500/20 text-red-400' :
                      metadata.inputType === 'file_upload' ? 'bg-orange-500/20 text-orange-400' :
                      metadata.inputType === 'app_share'   ? 'bg-cyan-500/20 text-cyan-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>{metadata.inputType}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="bg-gray-800 p-3 rounded-lg space-y-2 text-xs text-gray-400">
            <div>Created: {new Date(log.created_at).toLocaleString()}</div>
            <div>IP: {metadata?.headers?.realIp}</div>
            <div>Agent: {metadata?.headers?.userAgent}</div>
          </div>

          {onDeleteLog && (
            <div className="pt-3 mt-3 border-t border-gray-600">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteLog(log.request_id);
                }}
                disabled={isDeleting}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    <span>Delete Entry</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MobileLogCard;